import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Coins, AlertCircle, Sparkles } from 'lucide-react';

const CreditConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  credits = 1,
  toolName = 'this tool',
  currentCredits = 0 
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasEnoughCredits = currentCredits >= credits;

  const modalContent = (
    <div 
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-md"
        style={{ zIndex: 9999 }}
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden"
        style={{ zIndex: 10000 }}
      >
        {/* Header gradient */}
        <div className="h-1.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500" />
        
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6">
          {/* Icon */}
          <div className="flex justify-center mb-5">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
              hasEnoughCredits 
                ? 'bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/25' 
                : 'bg-gradient-to-br from-orange-500 to-red-500 shadow-lg shadow-orange-500/25'
            }`}>
              {hasEnoughCredits ? (
                <Sparkles className="w-7 h-7 text-white" />
              ) : (
                <AlertCircle className="w-7 h-7 text-white" />
              )}
            </div>
          </div>

          {/* Content */}
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {hasEnoughCredits ? 'Confirm Generation' : 'Insufficient Credits'}
            </h3>
            <p className="text-gray-500 text-sm">
              {hasEnoughCredits 
                ? `Using ${toolName} will consume ${credits} credit${credits > 1 ? 's' : ''}.`
                : `You need ${credits} credit${credits > 1 ? 's' : ''} but only have ${currentCredits}.`
              }
            </p>
          </div>

          {/* Credit info box */}
          <div className={`p-4 rounded-xl mb-6 ${
            hasEnoughCredits ? 'bg-cyan-50 border border-cyan-100' : 'bg-orange-50 border border-orange-100'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Coins className={`w-5 h-5 ${hasEnoughCredits ? 'text-cyan-600' : 'text-orange-600'}`} />
                <span className="text-sm font-medium text-gray-700">Your Credits</span>
              </div>
              <span className={`text-lg font-bold ${hasEnoughCredits ? 'text-cyan-600' : 'text-orange-600'}`}>
                {currentCredits}
              </span>
            </div>
            {hasEnoughCredits && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-cyan-200">
                <span className="text-xs text-gray-500">After generation</span>
                <span className="text-sm font-semibold text-gray-700">{currentCredits - credits}</span>
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            {hasEnoughCredits ? (
              <button
                onClick={onConfirm}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all shadow-md shadow-cyan-500/25"
              >
                Generate
              </button>
            ) : (
              <button
                onClick={onClose}
                className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-red-600 transition-all"
              >
                Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default CreditConfirmModal;
